import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import GeneralService from '../../../../../services/general';
import ErrorService from '../../../../../services/general/error';

// props

// components
import { Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import OrderTruckService from '../../../../../services/orders/trucks';
import { PPNPercentageOptions, PPhPercentageOptions } from '../../../../../manifests/taxPercentage';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import PricingService from '../../../../../services/general/pricing';
import { IOrderTruckResourceProps } from '../../../../../props/orders/trucks';
import PermissionsService from '../../../../../services/permissions';
import Details from '../../../../typography/details';

interface IOrderTruckFormProps {
    orderId: string;
    truckId?: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    name: string;
    vehicleRegistrationNumber: string;
    driverName: string;
    originAddress: string;
    destinationAddress: string;
    notes: string;
    price: string;
    ppn: string;
    pph: string;
    includePPn?: boolean;
    includePPh?: boolean;
    ppnPercentage: string;
    pphPercentage: string;
    finalPrice: string;
    nettPrice: string;
    invoicePrice: string;
}

type FormDataErrorProps = {
    name?: string;
    originAddress?: string;
    destinationAddress?: string;
    notes?: string;
    price?: string;
    ppn?: string;
    pph?: string;
}

const OrderTruckForm: React.FC<IOrderTruckFormProps> = (props: IOrderTruckFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [updatePrice, setUpdatePrice] = React.useState<boolean>(false);
    const [truck, setTruck] = React.useState<IOrderTruckResourceProps | undefined>();
    const [data, setData] = React.useState<FormDataProps>({
        name: "",
        vehicleRegistrationNumber: "",
        driverName: "",
        originAddress: "",
        destinationAddress: "",
        notes: "",
        price: "0",
        ppn: "0",
        pph: "0",
        ppnPercentage: "0",
        pphPercentage: "0",
        finalPrice: "0",
        nettPrice: "0",
        invoicePrice: "0"
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const mode: 'create' | 'update' = props.truckId === undefined ? 'create' : 'update';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        if (props.truckId) {
            const _truck = await OrderTruckService.get(props.orderId, props.truckId);

            setData({
                name: _truck.name || "",
                vehicleRegistrationNumber: _truck.vehicleRegistrationNumber || "",
                driverName: _truck.driverName || "",
                originAddress: _truck.originAddress || "",
                destinationAddress: _truck.destinationAddress || "",
                notes: _truck.notes || "",
                price: Number(_truck.price) + "",
                ppn: Number(_truck.ppn) + "",
                pph: Number(_truck.pph) + "",
                ppnPercentage: Number(_truck.ppnPercentage) + "",
                pphPercentage: Number(_truck.pphPercentage) + "",
                finalPrice: _truck.totalPrice + "",
                nettPrice: _truck.nettPrice + "",
                invoicePrice: _truck.invoicePrice + "",
                includePPn: _truck.includePPn,
                includePPh: _truck.includePPh
            });
            setTruck(_truck);
        }

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        const hasErrors = Boolean(
            (error.destinationAddress && error.destinationAddress.trim() !== '') ||
            (error.notes && error.notes.trim() !== '') ||
            (error.originAddress && error.originAddress.trim() !== '') ||
            (error.name && error.name.trim() !== '')
        );

        if (hasErrors) {
            return true;
        }

        if (data.name.trim() === '' || data.originAddress.trim() === "" || data.destinationAddress.trim() === "") {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, vehicleRegistrationNumber, driverName, originAddress, destinationAddress, notes, price, ppn, pph, includePPn, includePPh, ppnPercentage, pphPercentage, finalPrice, nettPrice, invoicePrice } = data;

            // create form data
            let fd = new FormData();
            fd.append("name", name);
            fd.append("vehicleRegistrationNumber", vehicleRegistrationNumber || "");
            fd.append("driverName", driverName || "");
            fd.append("originAddress", originAddress);
            fd.append("destinationAddress", destinationAddress);
            fd.append("notes", notes);
            fd.append("price", price);

            if (updatePrice || props.truckId === undefined) {
                fd.append("totalPrice", finalPrice);
                fd.append("finalPrice", finalPrice);
                fd.append("nettPrice", nettPrice);
                fd.append("invoicePrice", invoicePrice);
                fd.append("price", price);
                fd.append("ppn", ppn);
                fd.append("pph", pph);
                fd.append("includePPn", includePPn + "");
                fd.append("includePPh", includePPh + "");
                fd.append("ppnPercentage", ppnPercentage + "");
                fd.append("pphPercentage", pphPercentage + "");

                if (truck?.invoiceId) {
                    fd.append("updateInvoice", "1");
                }
            }

            if (props.truckId === undefined) {
                await OrderTruckService.new(props.orderId, fd);
            } else {
                await OrderTruckService.update(props.orderId, props.truckId, fd);
            }

            banner.add({
                key: mode + '_order_truck_success',
                variant: 'success',
                icon: faCheck,
                text: `Truck "${name}" ${mode === 'create' ? 'created' : 'updated'} successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: mode + '_order_truck_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to ${mode} truck. Error: ` + ErrorService.getMessage(e),
            });
        }
    }

    const calculateAllPrice = (_data: FormDataProps) => {
        const pricing = PricingService.order.calculate(_data, 'lumpsum');
        setData({ ..._data, ...pricing });
    }

    return <Panel headerText={mode === 'create' ? 'Create Truck' : 'Update Truck'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating truck ..." : "Updating truck ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <Stack.Item>
                        <TextField label={"Name"}
                            required={true}
                            value={data.name}
                            onChange={(evt, value) => {
                                data.name = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.name = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField 
                                label={"Nomor Polisi (Nopol)"}
                                placeholder="cth: B 1234 CD"
                                value={data.vehicleRegistrationNumber}
                                onChange={(evt, value) => {
                                    data.vehicleRegistrationNumber = value || "";
                                    setData({ ...data });
                                }}
                                disabled={submitting} 
                            />
                        </Stack.Item>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField 
                                label={"Pengemudi / Sopir"}
                                placeholder="Nama supir"
                                value={data.driverName}
                                onChange={(evt, value) => {
                                    data.driverName = value || "";
                                    setData({ ...data });
                                }}
                                disabled={submitting} 
                            />
                        </Stack.Item>
                    </Stack>
                    <Stack.Item>
                        <TextField label={"Pick-up Address"}
                            multiline
                            resizable={false}
                            rows={3}
                            autoAdjustHeight
                            required={true}
                            value={data.originAddress}
                            onChange={(evt, value) => {
                                data.originAddress = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.originAddress = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.originAddress}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Drop-off Address"}
                            multiline
                            resizable={false}
                            rows={3}
                            autoAdjustHeight
                            required={true}
                            value={data.destinationAddress}
                            onChange={(evt, value) => {
                                data.destinationAddress = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.destinationAddress = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.destinationAddress}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Notes"}
                            multiline
                            resizable={false}
                            rows={3}
                            autoAdjustHeight
                            value={data.notes}
                            onChange={(evt, value) => {
                                data.notes = value || "";

                                const validation = ValidationService.combination(value, ['limit'], { maxChars: 100 });
                                error.notes = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.notes}
                            disabled={submitting} />
                    </Stack.Item>
                    {/*<Stack className={'divider'}></Stack>
                    <Checkbox label={'Update price'}
                        checked={updatePrice}
                        disabled={(truck?.invoiceId || "").trim() !== ""  && !PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions)}
                        onChange={(ev, checked) => setUpdatePrice(checked || false)} />
                    {truck?.invoiceId && updatePrice ? <Text className={'color-yellow'}>!!! WARNING !!! This truck already associated with an invoice, updating this truck's price will also update invoice amount.</Text> : null}
                    <Stack.Item>
                        <TextField label={"Price"}
                            value={data.price}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.price = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.price = validation.message;

                                    setError({ ...error });

                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            description={data.price !== "" ? GeneralService.convertNumberToWords(Number(data.price)) : undefined}
                            errorMessage={error.price}
                            disabled={submitting || !updatePrice}
                            required={true} />
                    </Stack.Item>
                    <Stack tokens={{ childrenGap: 20 }} horizontal>
                        <Stack.Item>
                            <Checkbox checked={data.includePPn}
                                label='Include PPn'
                                disabled={submitting || !updatePrice}
                                onChange={(ev, checked) => {
                                    data.includePPn = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                        <Stack.Item>
                            <Checkbox checked={data.includePPh}
                                disabled={submitting || !updatePrice}
                                label='Include PPh'
                                onChange={(ev, checked) => {
                                    data.includePPh = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPn</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPNPercentageOptions}
                                    selectedKey={data.ppnPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.ppnPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.ppn}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.ppn = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.ppn = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.ppn}
                                    disabled={submitting || data.ppnPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPh</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPhPercentageOptions}
                                    selectedKey={data.pphPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.pph = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.pphPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.pph}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.pph = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.pph = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.pph}
                                    disabled={submitting || data.pphPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Total price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.finalPrice)}`} style={{width: '50%'}} />
                        <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.invoicePrice)}`} style={{width: '50%'}} />
                    </Stack>*/}
                </> : null
            }
        </Stack>
    </Panel >
};

export default OrderTruckForm;
